import Modal from '@orn/shared-web/components/Modal/Modal';
import * as React from 'react';

import Button from '../Button';

/**
 * Shared typed-confirmation dialog for high-blast-radius admin actions.
 * Domain-agnostic: any caller that needs friction proportional to the
 * blast radius of an action can use this dialog by passing the value
 * the operator must type to confirm. Today the Feature Gates admin
 * uses it for disable + delete flows; future callers may use it to
 * confirm a destructive impersonation, a bulk delete, etc.
 *
 * The friction is deliberate: a misclick on a destructive admin action
 * can affect every user simultaneously, so the operator must type the
 * `confirmValue` string verbatim before the destructive button enables.
 * Case-sensitive on purpose.
 *
 * Loading state is owned here, not by the parent: while `onConfirm`'s
 * promise is in flight the input goes read-only and the action button
 * shows a spinner. The parent only sees the resolved/rejected promise.
 */

interface IDangerConfirmDialogProps {
  /** Controls modal visibility. Parent owns the open state. */
  isOpen: boolean;
  /** Forwarded to react-aria's overlay so Esc / outside-click flow back. */
  onOpenChange: (isOpen: boolean) => void;
  /**
   * The exact string the operator must type to enable the destructive
   * button. Rendered in the prompt label and as the input placeholder.
   * Case-sensitive equality.
   */
  confirmValue: string;
  /** Header text — e.g. "Disable feature gate" or "Delete feature gate". */
  title: string;
  /** Body of the dialog — explains the consequences. Can include emphasis. */
  body: React.ReactNode;
  /** Label on the destructive submit button — e.g. "Disable" / "Delete". */
  confirmLabel: string;
  /** Async handler. The dialog awaits the promise; rejection keeps it open. */
  onConfirm: () => Promise<void>;
}

/**
 * Renders the dialog. The submit button is disabled until the typed
 * input string-equals `confirmValue`; on submit the dialog awaits
 * `onConfirm` and closes itself on success. On rejection it stays open
 * with the typed input intact so the operator can retry without
 * re-typing.
 */
const DangerConfirmDialog = ({
  isOpen,
  onOpenChange,
  confirmValue,
  title,
  body,
  confirmLabel,
  onConfirm,
}: IDangerConfirmDialogProps) => {
  const [typed, setTyped] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const inputId = React.useId();

  // Reset the typed input every time the dialog opens fresh. Keeps
  // a previous attempt from carrying over after Cancel + reopen.
  React.useEffect(() => {
    if (isOpen) {
      setTyped('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const isMatch = typed === confirmValue;
  const canSubmit = isMatch && !isSubmitting;

  const handleConfirm = async () => {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Caller is expected to surface a toast (Apollo errorLink covers
      // the Sentry leg). We keep the dialog open + typed value intact
      // so the operator can retry the destructive action without
      // re-typing from scratch.
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="sm">
      <div className="flex flex-col">
        <div className="border-fg-error-primary bg-bg-error-primary/10 border-b px-4 py-3">
          <span className="text-fg-error-primary text-xs font-semibold uppercase tracking-widest">{title}</span>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          <div className="text-fg-primary text-sm leading-relaxed">{body}</div>

          <div className="flex flex-col gap-1">
            <label htmlFor={inputId} className="text-fg-secondary text-xs font-medium uppercase tracking-wider">
              Type <span className="text-fg-primary font-mono normal-case tracking-normal">{confirmValue}</span> to
              confirm
            </label>
            <input
              id={inputId}
              type="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) {
                  void handleConfirm();
                }
              }}
              readOnly={isSubmitting}
              className="border-border-primary bg-bg-primary text-fg-primary placeholder:text-fg-quaternary focus-visible:outline-border-brand w-full border px-2.5 py-1.5 font-mono text-sm focus-visible:outline-2 focus-visible:-outline-offset-1"
              placeholder={confirmValue}
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button color="secondary" size="sm" onClick={() => onOpenChange(false)} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => void handleConfirm()}
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DangerConfirmDialog;
